import { BIOME } from "@eldrun/worldgen";
import { AMBIENCE_BEDS } from "../assets/sounds.generated.ts";
import { BED_IDS, type BedId } from "../audio/ambience-model.ts";
import { seamJump } from "../audio/audio-model.ts";
import { audio, type LoopHandle } from "../audio/engine.ts";
import { GameAudio, type AudioFrame } from "../audio/game-audio.ts";
import { BedPreviews } from "./bed-previews.ts";

/** Nomes dos leitos para quem escuta. */
const BED_NAMES: Record<BedId, string> = {
  "floresta-dia": "Floresta de dia",
  "floresta-noite": "Floresta de noite",
  praia: "Praia",
  mar: "Mar aberto",
  "mar-tempestade": "Mar na tempestade",
};
/** Biomas do simulador: o nome que o jogador vê e o id interno. */
const BIOMES = [
  ["mata", "Floresta"],
  ["cerrado", "Charneca"],
  ["mangue", "Pântano"],
  ["serra", "Montanhas"],
  ["praia", "Costa"],
] as const satisfies readonly (readonly [keyof typeof BIOME, string])[];
const RAFT = [
  ["", "Sem Jangada"],
  ["contagem", "Contagem do Zarpar"],
  ["travessia", "Travessia"],
  ["ancorada", "Ancorada (Dragão)"],
] as const;

/** Emenda medida num loop decodificado: taxa, canais, tamanho, pontos de loop (s) e os degraus. */
export interface SeamCheck {
  rate: number;
  channels: number;
  frames: number;
  loopStart: number;
  loopEnd: number;
  jump: number;
  p99: number;
  max: number;
  decodeMs?: number;
}

interface Sim {
  land: boolean;
  coast: number;
  biome: (typeof BIOMES)[number][0];
  night: number;
  raft: string;
}

const pct = (v: number | undefined) => `${Math.round((v ?? 0) * 100)} %`;
const num = (v: number, digits = 3) => v.toFixed(digits).replace(".", ",");

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (text !== undefined) e.textContent = text;
  return e;
}

function button(text: string, onClick: () => void, title?: string): HTMLButtonElement {
  const b = el("button", "small", text);
  b.type = "button";
  if (title) b.title = title;
  b.addEventListener("click", onClick);
  return b;
}

/** O quadro que o simulador entrega ao áudio da partida: um ouvinte parado, sem corpo, no contexto escolhido. */
function simFrame(now: number, s: Sim): AudioFrame {
  const night = s.night >= 0.999;
  return {
    now,
    listener: { x: 0, y: 2, z: 0, fx: 0, fy: 0, fz: -1, yaw: 0 },
    self: undefined,
    others: [],
    underwater: false,
    hp: 1,
    spectating: false,
    lowHunger: false,
    exhausted: false,
    drowning: false,
    criticalHp: false,
    night,
    dusk: !night && s.night > 0,
    land: s.land,
    nightBlend: s.night,
    bosses: [],
    challenge: false,
    raftStage: s.raft || undefined,
    treta: false,
    cerco: undefined,
    whirlwinds: [],
    creatures: [],
    fires: [],
    biome: () => BIOME[s.biome],
    // A linha da costa fica para −x: as ondas sintetizadas vêm desse lado.
    coast: (x) => Math.max(0, s.coast + x),
    seed: 1,
  };
}

/**
 * Leitos de ambiente na bancada (`?debug=sons`, F46): cada leito sozinho (do começo do loop ou 2 s
 * antes da emenda), o fogo em loop, a conferência das emendas decodificadas no navegador e um
 * simulador de contexto que roda o áudio da partida (leitos e a síntese por baixo, sem a trilha)
 * com a costa, a hora, o bioma e a Jangada escolhidos, mostrando a mistura e o LRU.
 */
export function mountAmbienceBench(root: HTMLElement): { stats(): Record<string, unknown> } {
  const checks: Partial<Record<BedId | "fogo", SeamCheck | { error: string }>> = {};
  let checked = false;
  let fire: LoopHandle | undefined;
  let game: GameAudio | undefined;
  let raf = 0;
  const sim: Sim = { land: true, coast: 200, biome: "mata", night: 0, raft: "" };

  const section = el("section", "leitos");
  section.append(el("h3", undefined, "Leitos de ambiente"));
  section.append(el("p", "nota", "Loops estéreo de 30 s (crossfade de 2 s na emenda), baixados e decodificados quando tocam, no máximo 3 decodificados ao mesmo tempo. Tocar: o leito sozinho, no começo do loop. Emenda: 2 s antes do fim do loop, para ouvir a volta. O fogo é o loop do banco de efeitos (fogueiras, Fornos, Panelas e o anel do Cerco)."));

  // Leitos e o fogo: tocar, emenda, estado e a emenda medida.
  const table = el("table");
  const head = el("tr");
  for (const h of ["Leito", "Tocar", "Estado", "Emenda medida (degrau · p99)"]) head.append(el("th", undefined, h));
  table.append(el("thead"));
  table.tHead!.append(head);
  const body = el("tbody");
  const rows = new Map<BedId | "fogo", { play: HTMLButtonElement; state: HTMLElement; seam: HTMLElement }>();
  // Prévias: no máximo 3 juntas; um pedido que chega depois de outro do mesmo leito para na hora.
  const previews = new BedPreviews(
    (id, seam) => audio.previewBed(id, seam),
    (id, on) => (rows.get(id)!.play.textContent = on ? "Parar" : "Tocar"),
  );
  const stopFire = () => {
    fire?.stop(0.3);
    fire = undefined;
    rows.get("fogo")!.play.textContent = "Tocar";
  };
  const preview = (id: BedId, seam: boolean) => {
    audio.unlock();
    stopSim();
    void previews.play(id, seam);
  };
  const playFire = (seam: boolean) => {
    audio.unlock();
    stopSim();
    stopFire();
    const handle = audio.loop("fogo", "efeitos", { seam });
    if (!handle.live) return;
    handle.set(undefined, 0, 0, 1);
    fire = handle;
    rows.get("fogo")!.play.textContent = "Parar";
  };
  for (const id of [...BED_IDS, "fogo" as const]) {
    const row = el("tr");
    const name = el("td");
    name.append(el("strong", undefined, id === "fogo" ? "Fogo" : BED_NAMES[id]));
    name.append(el("small", undefined, id === "fogo" ? "fogo · mono, loop de 8 s" : `${id} · estéreo, loop de ${num(AMBIENCE_BEDS[id].loopEnd - AMBIENCE_BEDS[id].loopStart, 0)} s`));
    const buttons = el("td", "botoes");
    const play = button("Tocar", () => {
      if (id === "fogo") return fire ? stopFire() : playFire(false);
      if (previews.playing().includes(id)) previews.stop(id);
      else preview(id, false);
    });
    buttons.append(play, button("Emenda", () => (id === "fogo" ? playFire(true) : preview(id, true)), "Começa 2 s antes do fim do loop: a volta ao começo vem logo depois"));
    const state = el("td", "nivel", "—");
    const seam = el("td", "nivel", "—");
    row.append(name, buttons, state, seam);
    body.append(row);
    rows.set(id, { play, state, seam });
  }
  table.append(body);
  section.append(table);

  // Conferência das emendas: cada leito decodificado (o LRU despeja no caminho) e o fogo.
  const checkButton = button("Conferir as emendas", () => void checkSeams(), "Decodifica cada leito e mede o degrau na emenda contra o p99 dos degraus em volta");
  section.append(checkButton);
  const measure = (buffer: AudioBuffer, loopStart: number, loopEnd: number): SeamCheck => {
    const channels = Array.from({ length: buffer.numberOfChannels }, (_, c) => buffer.getChannelData(c));
    const s = seamJump(channels, Math.round(loopStart * buffer.sampleRate), Math.round(loopEnd * buffer.sampleRate), Math.round(0.05 * buffer.sampleRate));
    return { rate: buffer.sampleRate, channels: buffer.numberOfChannels, frames: buffer.length, loopStart, loopEnd, ...s };
  };
  const showSeam = (id: BedId | "fogo") => {
    const c = checks[id];
    rows.get(id)!.seam.textContent = !c ? "—" : "error" in c ? c.error : `${num(c.jump, 4)} · ${num(c.p99, 4)} ${c.jump <= c.p99 ? "contínua" : "DEGRAU"}`;
  };
  async function checkSeams(): Promise<void> {
    checkButton.disabled = true;
    stopSim();
    previews.stopAll();
    await audio.prerender();
    const bank = audio.ambienceBank;
    for (const id of BED_IDS) {
      bank.pin(id);
      const bed = await bank.whenReady(id);
      const decodeMs = bank.stats().decodeMs[id];
      checks[id] = bed ? { ...measure(bed.buffer, bed.loopStart, bed.loopEnd), ...(decodeMs !== undefined && { decodeMs }) } : { error: "indisponível" };
      bank.unpin(id);
      showSeam(id);
    }
    const shape = audio.sampleShape("fogo");
    const fogo = audio.variations("fogo", "amostra")[0];
    checks.fogo = fogo && shape?.loopEnd !== undefined ? measure(fogo, shape.loopStart ?? 0, shape.loopEnd) : { error: "sem amostra (síntese)" };
    showSeam("fogo");
    checked = true;
    checkButton.disabled = false;
  }

  // Simulador de contexto: o áudio da partida com a costa, a hora, o bioma e a Jangada daqui.
  const form = el("fieldset", "simulador");
  form.append(el("legend", undefined, "Simulador de contexto"));
  const field = (label: string, input: HTMLElement, out?: HTMLElement) => {
    const l = el("label");
    l.append(el("span", undefined, label), input);
    if (out) l.append(out);
    form.append(l);
  };
  const place = el("select");
  for (const [v, t] of [["terra", "Em terra"], ["mar", "No mar"]] as const) place.append(new Option(t, v));
  place.addEventListener("change", () => (sim.land = place.value === "terra"));
  field("Lugar", place);
  const coast = el("input");
  coast.type = "range";
  coast.min = "0";
  coast.max = "120";
  coast.value = "120";
  const coastOut = el("output", undefined, "120 m");
  const setCoast = () => {
    // No fim da régua, longe da costa (200 m): a praia some e o mar fica aberto.
    sim.coast = coast.value === "120" ? 200 : Number(coast.value);
    coastOut.textContent = coast.value === "120" ? "longe" : `${coast.value} m`;
  };
  coast.addEventListener("input", setCoast);
  setCoast();
  field("Distância da costa", coast, coastOut);
  const biome = el("select");
  for (const [v, t] of BIOMES) biome.append(new Option(t, v));
  biome.addEventListener("change", () => (sim.biome = biome.value as Sim["biome"]));
  field("Bioma", biome);
  const night = el("input");
  night.type = "range";
  night.min = "0";
  night.max = "100";
  night.value = "0";
  const nightOut = el("output", undefined, "Dia");
  night.addEventListener("input", () => {
    sim.night = Number(night.value) / 100;
    nightOut.textContent = sim.night === 0 ? "Dia" : sim.night >= 1 ? "Noite" : `Boca da Noite (${night.value} %)`;
  });
  field("Hora", night, nightOut);
  const raft = el("select");
  for (const [v, t] of RAFT) raft.append(new Option(t, v));
  raft.addEventListener("change", () => (sim.raft = raft.value));
  field("Jangada", raft);
  const simButton = button("Simular", () => (game ? stopSim() : startSim()), "Toca os leitos e a síntese por baixo como na partida (sem a trilha)");
  form.append(simButton);
  section.append(form);

  // Leitura ao vivo: a mistura, a síntese por baixo, o banco (LRU) e as esperas.
  const mixTable = el("table");
  const mixHead = el("tr");
  for (const h of ["Leito", "Alvo", "Nível", "Tocando", "Banco"]) mixHead.append(el("th", undefined, h));
  mixTable.append(el("thead"));
  mixTable.tHead!.append(mixHead);
  const mixBody = el("tbody");
  const mixCells = new Map<BedId | "nenhum", HTMLElement[]>();
  for (const id of [...BED_IDS, "nenhum" as const]) {
    const row = el("tr");
    const cells = [el("td", undefined, id === "nenhum" ? "Sem leito (só a síntese)" : BED_NAMES[id]), el("td", "nivel"), el("td", "nivel"), el("td", "nivel"), el("td", "nivel")];
    row.append(...cells);
    mixBody.append(row);
    mixCells.set(id, cells);
  }
  mixTable.append(mixBody);
  section.append(mixTable);
  const synthLine = el("p", "nota");
  const bankLine = el("p", "nota");
  const latencyLine = el("p", "nota");
  section.append(synthLine, bankLine, latencyLine);

  function startSim(): void {
    audio.unlock();
    previews.stopAll();
    stopFire();
    game = new GameAudio(audio, { music: false });
    simButton.textContent = "Parar a simulação";
    const tick = () => {
      if (!game) return;
      game.update(simFrame(performance.now(), sim));
      raf = requestAnimationFrame(tick);
    };
    tick();
  }
  function stopSim(): void {
    if (!game) return;
    cancelAnimationFrame(raf);
    // O fim da cena para as prévias no motor (`game.dispose`); aqui, também a lista delas.
    previews.stopAll();
    game.dispose();
    game = undefined;
    simButton.textContent = "Simular";
  }

  const refresh = () => {
    const a = audio.stats().ambience;
    for (const [id, cells] of mixCells) {
      cells[1]!.textContent = pct(a.targets?.[id]);
      cells[2]!.textContent = pct(a.levels?.[id]);
      cells[3]!.textContent = id === "nenhum" ? "—" : a.voices.includes(id) ? "sim" : a.playing.includes(id) ? "esperando" : "não";
      cells[4]!.textContent = id === "nenhum" ? "—" : a.states[id];
    }
    for (const id of BED_IDS) {
      const ms = a.decodeMs[id];
      rows.get(id)!.state.textContent = `${a.states[id]}${ms !== undefined ? ` · decodificou em ${ms} ms` : ""}`;
    }
    rows.get("fogo")!.state.textContent = audio.variations("fogo", "amostra").length ? "amostra gravada" : "síntese (sem a amostra)";
    const c = a.cover;
    synthLine.textContent = c ? `Síntese por baixo: vento ${num(c.wind)} (base 0,120) · pássaros ${pct(c.birds)} · grilos e sapos ${pct(c.night)} · ondas ${pct(c.waves)}` : "Síntese por baixo: — (simulação parada)";
    bankLine.textContent = `Banco (${a.format.toUpperCase()}): decodificados, do usado há mais tempo: ${a.decoded.join(", ") || "nenhum"} · presos: ${a.pinned.join(", ") || "nenhum"} · ${num(a.cachedBytes / 1024, 0)} KiB comprimidos guardados · ${num(a.pcmBytes / 1e6, 1)} MB de PCM · ${a.decodes} decodificações, ${a.decodesDuringRender} durante o render · no máximo ${a.maxDecoded} decodificados juntos${a.error ? ` · ${a.error}` : ""}`;
    latencyLine.textContent = `Do pedido ao som: ${a.latency.map((l) => `${BED_NAMES[l.bed]} ${l.ms} ms (${l.kind})`).join(" · ") || "—"}`;
  };
  refresh();
  setInterval(refresh, 250);

  root.append(section);
  return {
    stats: () => ({ beds: { ...checks }, bedsChecked: checked, simulating: !!game, ambience: audio.stats().ambience }),
  };
}
